import { Component, ComponentFactoryResolver, ComponentRef, Injector, Input, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { PluginService } from '../../services/plugin.service';
import { Plugin } from '../../classes/plugin';
import { upperCaseFirst } from '../../../support/helpers';

@Component({
    selector: 'plugin',
    templateUrl: './plugin.component.html',
    styleUrls: ['./plugin.component.scss'],
})
export class PluginComponent implements OnInit, OnDestroy {

    @Input() plugin: Plugin;

    /**
     * The name of the slot.
     */
    @Input() name: string;

    /**
     * The data that will be passed trough by the plugin slot.
     */
    @Input() data: any;

    /**
     * The ViewContainerRef instance.
     */
    @ViewChild('content', {read: ViewContainerRef}) content: ViewContainerRef;

    /**
     * The ComponentFactoryResolver instance.
     */
    protected componentFactoryResolver: ComponentFactoryResolver;

    /**
     * The PluginService instance.
     */
    protected pluginService: PluginService;

    /**
     * The plugin observer subscription to watch changes.
     */
    protected subscription;

    /**
     * The rendered component reference.
     */
    protected contentRef: ComponentRef<any>;

    /**
     * The rendered component instance.
     */
    protected contentInstance: any;

    /**
     * The rendered component reference.
     */
    protected templateRef: ComponentRef<any>;

    /**
     * The rendered component instance.
     */
    protected templateInstance: any;

    constructor(protected injector: Injector) {
        this.componentFactoryResolver = this.injector.get(ComponentFactoryResolver);
        this.pluginService            = this.injector.get(PluginService);
    }

    ngOnInit() {
        this.prepareData();

        this.renderTemplate();
        this.renderContent();
        this.bindTemplateReferences();
    }

    protected renderTemplate() {
        if (!this.plugin.template) {
            throw new Error('No template was provided for template.');
        }

        // Get the component from the component factory resolver
        const childComponent = this.componentFactoryResolver.resolveComponentFactory(this.plugin.template);

        // Create an instance of the component
        const reference = this.templateRef = this.content.createComponent(childComponent);

        // Get the component instance
        this.templateInstance = reference.instance;

        // Bind the plugin data to the plugin content component
        this.bindPropertyToTemplate('data', this.plugin.data);
    }

    /**
     * Bind a property to the template.
     *
     * @param {string} name
     * @param value
     */
    protected bindPropertyToTemplate(name: string, value: any) {
        this.templateInstance[name] = value;
    }

    protected renderContent() {
        if (!this.plugin.template) {
            throw new Error('No template was provided for plugin.');
        }

        const childComponent = this.componentFactoryResolver.resolveComponentFactory(this.plugin.component);

        // Create an instance of the component
        const reference = this.contentRef = this.templateInstance.content.createComponent(childComponent);

        // Get the plugin component instance
        this.contentInstance = reference.instance;

        // Bind the plugin data to the plugin content component
        this.bindPropertyToContent('data', this.plugin.data);

        this.bindDataProperties();
    }


    /**
     * Bind properties to the content.
     */
    protected bindTemplateReferences() {
        if (!this.templateInstance) {
            return;
        }

        if (!this.contentInstance) {
            return;
        }

        // Get all properties of the template instance
        const properties = Object.keys(this.contentInstance);

        // Check for each property if it is a valid template reference, if so render it's contents.
        properties.forEach((name) => {
            if (this.isTemplateReference(name)) {
                this.bindTemplateReference(name);
            }
        });

        this.templateInstance['hasContent'] = true;
    }

    /**
     * Indicates if the given property is a property which can be used to create a reference for
     * templates between the plugin content and the plugin template.
     *
     * @param {string} name
     * @returns {boolean}
     */
    protected isTemplateReference(name: string) {
        return !!(
            this.contentInstance[name] &&
            this.contentInstance[name] instanceof Object &&
            (Object.getPrototypeOf(this.contentInstance[name]) instanceof TemplateRef)
        );
    }

    /**
     * Creates a reference for a template between the plugin content and the plugin template on the
     * given property.
     *
     * @param {string} name
     */
    protected bindTemplateReference(name: string) {
        if (this.contentInstance[name]) {
            this.templateInstance['has' + upperCaseFirst(name)] = true;
        }

        this.templateInstance[name] = this.contentInstance[name];
    }

    /**
     * Merge the plugin slot's data with the plugin data.
     */
    protected prepareData() {
        if (!this.data) {
            this.data = {};
        }

        if (!this.data.inputs) {
            this.data.inputs = {};
        }

        if (!this.data.outputs) {
            this.data.outputs = {};
        }

        if (this.plugin.inputs) {
            this.data.inputs = Object.assign(this.data.inputs, this.plugin.inputs);
        }

        if (this.plugin.outputs) {
            this.data.outputs = Object.assign(this.data.outputs, this.plugin.outputs);
        }
    }

    /**
     * Bind a property to the template instance.
     *
     * @param {string} name
     * @param value
     */
    protected bindTemplateProperty(name: string, value: any) {
        this.templateInstance[name] = value;
    }

    /**
     * Bind data properties to the template.
     */
    protected bindTemplateDataProperties() {
        if (this.data && this.data.inputs) {
            for (const key in this.data.inputs) {
                if (this.data.inputs.hasOwnProperty(key)) {
                    this.bindTemplateProperty(key, this.data.inputs[key]);
                }
            }
        }
    }

    /**
     * Create the data bindings.
     */
    protected bindDataProperties() {
        // Create input bindings
        if (this.data && this.data.inputs) {
            for (const key in this.data.inputs) {
                if (this.data.inputs.hasOwnProperty(key)) {
                    this.bindInputProperty(key);
                }
            }
        }

        // Create output bindings
        if (this.data && this.data.outputs) {
            for (const key in this.data.outputs) {
                if (this.data.outputs.hasOwnProperty(key)) {
                    this.bindOutputProperty(key);
                }
            }
        }
    }

    /**
     * Create an input binding.
     *
     * @param key
     */
    protected bindInputProperty(key) {
        this.contentInstance[key] = this.data.inputs[key];
    }

    /**
     * Create an output binding.
     *
     * @param key
     */
    protected bindOutputProperty(key) {
        this.contentInstance[key].subscribe((value) => {
            this.data.outputs[key](value);
        });
    }

    /**
     * Bind a property to the content.
     *
     * @param {string} name
     * @param value
     */
    protected bindPropertyToContent(name: string, value: any) {
        this.contentInstance[name] = value;
    }

    ngOnDestroy() {
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/plugins/components/plugin/plugin.component.ts